/**
 * Puces « Mes droits » du profil : union des contributions OIDC + périmètre manager (ancrage).
 * Réf. monorga-ihm/docs/role-capability-matrix.md (copie) + monorg-api/docs/role-capability-matrix.md (source)
 */
import type { MeOrgPosition } from '~/composables/equipe/useMyOrgPositions'
import { ERole } from './e_roles'
import {
  AppCapability,
  canAdminTagsRoleSet,
  OIDC_PROFILE_MANAGER,
  userHasCapability,
} from './roleCapabilityMatrix'

export type OrgAnchorLevel = 'direction' | 'domaine' | 'agence' | 'equipe' | 'section'

const CHIP_BY_CAPABILITY: Partial<Record<AppCapability, string>> = {
  [AppCapability.ORG_READ]: 'Accès à l’organigramme (lecture / navigation)',
  [AppCapability.ORG_EDIT_DIRECTION]: 'Modification direction',
  [AppCapability.ORG_EDIT_DOMAINE]: 'Modification domaine',
  [AppCapability.ORG_REORDER_DOMAINE]: 'Réordonner les domaines',
  [AppCapability.ORG_EDIT_AGENCE]: 'Modification agence',
  [AppCapability.ORG_EDIT_EQUIPE]: 'Modification équipe',
  [AppCapability.ADMIN_TAGS]: 'Gestion des tags (admin)',
  [AppCapability.ADMIN_TECH_TOOLS]: 'Outils techniques (jobs, maintenance, notice, …)',
}

const MANAGER_CHILDREN_BY_ANCHOR: Record<OrgAnchorLevel, OrgAnchorLevel[]> = {
  direction: ['domaine', 'agence', 'equipe', 'section'],
  domaine: ['agence', 'equipe', 'section'],
  agence: ['equipe', 'section'],
  equipe: ['section'],
  section: [],
}

const LEVEL_TO_EDIT_CAPABILITY: Record<OrgAnchorLevel, AppCapability | null> = {
  direction: AppCapability.ORG_EDIT_DIRECTION,
  domaine: AppCapability.ORG_EDIT_DOMAINE,
  agence: AppCapability.ORG_EDIT_AGENCE,
  equipe: AppCapability.ORG_EDIT_EQUIPE,
  section: null,
}

function trimSegment(value: string | undefined | null): string {
  if (typeof value !== 'string') {
    return ''
  }
  return value.trim()
}

/**
 * Ancrage déduit de la structure du rattachement, pas de la seule présence d’une section.
 * Équipe de direction (dir = domaine = agence = équipe), sinon équipe de domaine, sinon équipe d’agence, sinon équipe.
 * L’ancrage section du tableau est inatteignable.
 *
 * @param position - Rattachement section de l’utilisateur
 * @returns Niveau d’ancrage, ou null si l’équipe est vide
 */
export function inferOrgAnchorLevelForPosition(position: MeOrgPosition): OrgAnchorLevel | null {
  const dir = trimSegment(position.dirLib)
  const dom = trimSegment(position.domLib)
  const age = trimSegment(position.ageLib)
  const equ = trimSegment(position.equLib)
  if (dir !== '' && dir === dom && dom === age && age === equ) {
    return 'direction'
  }
  if (dom !== '' && dom === age && age === equ) {
    return 'domaine'
  }
  if (age !== '' && age === equ) {
    return 'agence'
  }
  if (equ !== '') {
    return 'equipe'
  }
  return null
}

function collectManagerModificationChips(
  positions: MeOrgPosition[],
  into: Set<string>
): void {
  const anchorsSeen = new Set<OrgAnchorLevel>()
  for (const position of positions) {
    const anchor = inferOrgAnchorLevelForPosition(position)
    if (anchor !== null) {
      anchorsSeen.add(anchor)
    }
  }
  for (const anchor of anchorsSeen) {
    const children = MANAGER_CHILDREN_BY_ANCHOR[anchor]
    for (const childLevel of children) {
      const capability = LEVEL_TO_EDIT_CAPABILITY[childLevel]
      if (capability !== null) {
        const label = CHIP_BY_CAPABILITY[capability]
        if (label) {
          into.add(label)
        }
      }
    }
  }
}

function addCapabilityChips(roles: string[], into: Set<string>): void {
  const orderedCaps: AppCapability[] = [
    AppCapability.ORG_READ,
    AppCapability.ORG_EDIT_DIRECTION,
    AppCapability.ORG_EDIT_DOMAINE,
    AppCapability.ORG_REORDER_DOMAINE,
    AppCapability.ORG_EDIT_AGENCE,
    AppCapability.ORG_EDIT_EQUIPE,
    AppCapability.ADMIN_TAGS,
    AppCapability.ADMIN_TECH_TOOLS,
  ]
  for (const capability of orderedCaps) {
    if (userHasCapability(roles, capability)) {
      const label = CHIP_BY_CAPABILITY[capability]
      if (label) {
        into.add(label)
      }
    }
  }
  if (canAdminTagsRoleSet(roles) && !userHasCapability(roles, AppCapability.ADMIN_TAGS)) {
    const tagLabel = CHIP_BY_CAPABILITY[AppCapability.ADMIN_TAGS]
    if (tagLabel) {
      into.add(tagLabel)
    }
  }
}

/**
 * Libellés des puces profil, stables, après union multi-profils et règle manager.
 * Pour YYX_ADMIN, les puces « modification … » reprennent la matrice OIDC ; l’édition réelle reste bornée au périmètre direction (rattachement organigramme, voir GET /v1/me).
 */
export function computeProfileRights(roles: string[], positions: MeOrgPosition[]): string[] {
  const labels = new Set<string>()
  addCapabilityChips(roles, labels)

  const hasManager = roles.includes(OIDC_PROFILE_MANAGER)
  const hasAdminLike =
    roles.includes(ERole.SUPER_ADMIN) ||
    roles.includes(ERole.ADMIN) ||
    roles.includes(ERole.DEVELOPER)

  if (hasManager && !hasAdminLike && positions.length > 0) {
    collectManagerModificationChips(positions, labels)
  }

  return [...labels]
}
